export interface SalonPublicBookingSnapshot {
  organization: {
    id: string;
    slug: string;
  };
  business: {
    publicName: string;
    publicDescription: string | null;
    phone: string | null;
    timezone: string;
    defaultCurrency: string;
    primaryLanguage: string;
  };
  publicBooking: {
    showPrices: boolean;
    allowProfessionalSelection: boolean;
    requireCustomerPhone: boolean;
  };
  bookingRules: {
    minimumNoticeMinutes: number;
    bookingHorizonDays: number;
    confirmationMode: "automatic" | "pending";
    depositMode: "none" | "percentage" | "fixed";
    depositValue: number;
  };
  services: readonly {
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    priceMinor: number | null;
    currency: string | null;
    locationIds: readonly string[];
    eligibleResourceIds: readonly string[];
  }[];
  locations: readonly {
    id: string;
    name: string;
    address: string | null;
    timezone: string;
  }[];
  professionals: readonly {
    id: string;
    name: string;
    locationIds: readonly string[];
  }[];
}

export interface SalonPublicSlot {
  locationId: string | null;
  resourceId: string | null;
  serviceId: string | null;
  startsAt: string;
  endsAt: string;
}

export interface SalonPublicAvailability {
  organizationSlug: string;
  timeZone: string;
  slots: readonly SalonPublicSlot[];
}

export type SalonPublicBookingResult =
  | {
      ok: true;
      appointment: {
        id: string;
        locationId: string | null;
        serviceId: string;
        resourceId: string | null;
        startsAt: string;
        endsAt: string;
        status: string;
      };
      idempotent: boolean;
      deposit:
        | {
            required: false;
            mode: "none";
            value: number;
            status: "not_required";
            amountMinor: null;
            currency: null;
            checkout: null;
            errorCode: null;
          }
        | {
            required: true;
            mode: "percentage" | "fixed";
            value: number;
            status: string;
            amountMinor: number | null;
            currency: string | null;
            checkout: {
              status: string;
              checkoutUrl: string;
            } | null;
            errorCode: string | null;
          };
    }
  | {
      ok: false;
      errorCode: "booking_conflict";
      alternatives: readonly SalonPublicSlot[];
    };

export class SalonPublicBookingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SalonPublicBookingApiError";
  }
}

type Fetcher = typeof fetch;

function safeSlug(value: string): string {
  if (!/^[a-z0-9](?:[a-z0-9-]{0,62})$/.test(value)) {
    throw new SalonPublicBookingApiError(
      400,
      "salon_slug_invalid",
      "El enlace de reserva no es válido.",
    );
  }
  return value;
}

export class SalonPublicBookingApi {
  private readonly fetcher: Fetcher;

  constructor(fetcher: Fetcher = fetch) {
    this.fetcher = fetcher.bind(globalThis);
  }

  private async request<T>(
    slug: string,
    suffix = "",
    init: RequestInit = {},
  ): Promise<T> {
    const safe = safeSlug(slug);
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }
    const response = await this.fetcher(
      `/api/v1/platform/public/salons/${encodeURIComponent(safe)}/booking${suffix}`,
      {
        ...init,
        credentials: "omit",
        headers,
      },
    );
    const body = (await response.json()) as {
      data?: T;
      error?: { code?: string; message?: string };
    };
    if (!response.ok) {
      if (
        response.status === 409 &&
        body.data &&
        typeof body.data === "object" &&
        "errorCode" in (body.data as object)
      ) {
        return body.data as T;
      }
      throw new SalonPublicBookingApiError(
        response.status,
        body.error?.code ?? "request_failed",
        body.error?.message ?? "No pudimos completar la solicitud.",
      );
    }
    return body.data as T;
  }

  snapshot(slug: string): Promise<SalonPublicBookingSnapshot> {
    return this.request(slug);
  }

  availability(
    slug: string,
    input: {
      serviceId: string;
      locationId?: string | null;
      resourceId?: string | null;
      startsAfter: string;
      endsBefore: string;
    },
  ): Promise<SalonPublicAvailability> {
    return this.request(slug, "/availability", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  createAppointment(
    slug: string,
    input: {
      serviceId: string;
      locationId?: string | null;
      resourceId?: string | null;
      startsAt: string;
      customerName: string;
      customerPhone?: string | null;
      customerEmail?: string | null;
    },
    idempotencyKey: string,
  ): Promise<SalonPublicBookingResult> {
    return this.request(slug, "/appointments", {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify(input),
    });
  }
}
